/**
 * features/documents/CustomerPicker.tsx — เลือกลูกค้าสำหรับฟอร์มเอกสาร
 *
 * ค้นจาก master data ที่มีอยู่ก่อน ถ้าไม่พบให้ค้นจากสรรพากรแล้วจะบันทึกลูกค้าใหม่ให้อัตโนมัติ
 * ข้อมูลลูกค้าที่เลือกจะ snapshot ลงในฟอร์ม ไม่ผูกกับ master data แบบ live
 */

import { formatBranch } from '@ft/core';
import { useDebounced } from '~/lib/use-debounced';
import { Combobox } from '~/ui/Combobox';
import { Field, Input, Textarea } from '~/ui/primitives';
import { TaxIdLookupField } from '~/features/tax-lookup/TaxIdLookupField';
import { useCustomerSearch } from '~/features/customers/use-customer-search';
import type { DocumentFormState } from './types';

interface CustomerPickerProps {
  form: DocumentFormState;
  onChange: (patch: Partial<DocumentFormState>) => void;
  error?: string;
}

export function CustomerPicker({ form, onChange, error }: CustomerPickerProps) {
  const debouncedName = useDebounced(form.customerName);
  const { data, isFetching } = useCustomerSearch(debouncedName);

  return (
    <div className="space-y-3">
      <Field label="ชื่อลูกค้า" required error={error}>
        <Combobox
          value={form.customerName}
          onChange={(value) => {
            onChange({ customerName: value, customerId: null });
          }}
          onSelect={(customer) => {
            onChange({
              customerId: customer.id,
              customerName: customer.name,
              customerAddress: customer.address,
              customerTaxId: customer.taxId,
              customerBranch: customer.branch,
              customerPhone: customer.phone,
            });
          }}
          options={data?.data ?? []}
          isLoading={isFetching}
          getLabel={(customer) => customer.name}
          getSublabel={(customer) => {
            const parts: string[] = [];
            if (customer.branch) parts.push(formatBranch(customer.branch));
            if (customer.taxId) parts.push(`เลขภาษี ${customer.taxId}`);
            if (customer.phone) parts.push(customer.phone);
            return parts.join(' • ');
          }}
          placeholder="ค้นหาลูกค้าที่มีอยู่ หรือพิมพ์ชื่อใหม่"
          invalid={Boolean(error)}
        />
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <TaxIdLookupField
          value={form.customerTaxId}
          onChange={(value) => onChange({ customerTaxId: value })}
          onFound={(branch) => {
            onChange({
              customerTaxId: branch.taxId,
              customerBranch: branch.branch,
              customerName: form.customerName || branch.name,
              customerAddress: branch.address || form.customerAddress,
              customerPhone: branch.phone || form.customerPhone,
            });
          }}
        />

        <Field label="สาขา (เช่น 00000 หรือ สำนักงานใหญ่)">
          <Input
            value={form.customerBranch}
            onChange={(event) => onChange({ customerBranch: event.target.value })}
            placeholder="00000 หรือ สำนักงานใหญ่"
          />
        </Field>
      </div>

      <Field label="เบอร์โทรลูกค้า">
        <Input
          value={form.customerPhone}
          onChange={(event) => onChange({ customerPhone: event.target.value })}
          placeholder="เช่น 02-xxx-xxxx หรือ 08x-xxx-xxxx"
        />
      </Field>

      <Field label="ที่อยู่ลูกค้า">
        <Textarea
          value={form.customerAddress}
          onChange={(event) => onChange({ customerAddress: event.target.value })}
          placeholder="ที่อยู่สำหรับออกเอกสารใบนี้"
        />
      </Field>
    </div>
  );
}
